/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { orderBy } from 'lodash';
import { format } from 'date-fns';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import {
  logListClick,
  logRoundProfile
} from '../../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../../utils/debouncers';
import { date } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Employee } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  CompleteIcon,
  DeptStartContainer,
  EmployeeInfoDiv,
  NewHireIconContainer,
  StartDateContainer
} from './AdhocEmployeesTable.styles';

interface AdhocEmployeesTableProps {
  adhocEmployees: Array<Employee>;
  isLoading: boolean;
  searchTerm: string;
}

const AdhocEmployeesTable: FC<AdhocEmployeesTableProps> = ({
  adhocEmployees,
  isLoading,
  searchTerm
}) => {
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [sortKey, setSortKey] = useState<string>('fullName');
  const [active, setActive] = useState<string>('Employee');

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const { logTrackingEvent } = useAnalyticsApi();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { '*': view } = useParams();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'fullName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'Dept Start':
        key = 'effectiveStartDate';
        break;
      case 'Rounded By':
        key = 'lastRoundedBy';
        break;
      case 'Last Round':
        key = 'lastRoundDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openEmployeeProfile = (employee: Employee) => {
    logTrackingEvent([...logListClick('table', view), ...logRoundProfile()]);
    navigate('/employees/profile', {
      state: {
        from: pathname,
        employeeId: employee.hcaid
      }
    });
  };

  const filteredEmployees = useMemo(() => {
    const q = new RegExp(searchTerm.trim().replace(/\\/g, ''), 'i');
    const filteredAdhocEmployees = adhocEmployees.filter(
      employee =>
        `${employee.firstName} ${employee.lastName}`.match(q) ||
        employee.hcaid.match(q) ||
        `${employee.lastName} ${employee.firstName}`.match(q)
    );
    return filteredAdhocEmployees;
  }, [dbSearchTerm, adhocEmployees]);

  const sortedEmployees = useMemo(() => {
    const filteredEmployeesCopy = [...filteredEmployees];
    const orderBySortDir = sortDir === 'asc' ? 'asc' : 'desc';
    const iterateByLowercase = (employee: Employee) =>
      (employee as any)[sortKey]?.toLowerCase() || '';

    return filteredEmployeesCopy.length
      ? orderBy(filteredEmployeesCopy, [iterateByLowercase], [orderBySortDir])
      : filteredEmployeesCopy;
  }, [filteredEmployees, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && sortedEmployees.length === 0,
    [dbSearchTerm, sortedEmployees]
  );

  const isTableEmptySearch = useMemo(
    () => dbSearchTerm && sortedEmployees.length === 0,
    [dbSearchTerm, sortedEmployees]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id="Employee-Table-Employee-Column"
      unit="column"
      colWidth={26}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="Department"
      id="Employee-Table-Department-Column"
      unit="column"
      colWidth={24}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Dept Start"
      id="Employee-Table-Dept-Start-Column"
      unit="column"
      colWidth={14}
      direction={!(active === 'Dept Start') ? 'none' : sortDir}
      sort
    >
      Dept Start
    </CellContainer>,
    <CellContainer
      key="Rounded By"
      id="Employee-Table-Rounded-By-Column"
      unit="column"
      colWidth={22}
      direction={!(active === 'Rounded By') ? 'none' : sortDir}
      sort
    >
      Rounded By
    </CellContainer>,
    <CellContainer
      key="Last Round"
      id="Employee-Table-Last-Round-Column"
      unit="column"
      colWidth={14}
      direction={!(active === 'Last Round') ? 'none' : sortDir}
      sort
    >
      Last Round
    </CellContainer>
  ];

  const rows: (boolean | JSX.Element)[] = sortedEmployees.map(
    (employee: Employee, i: number) => (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`AdHoc-List-Row-${i}`}
        id={`AdHoc-List-Row-${employee.hcaid}`}
        data-testid={`AdHoc-List-Row-${employee.hcaid}`}
        unit="row"
        size="default"
        height="5em"
        role="button"
        onClick={() => openEmployeeProfile(employee)}
      >
        <CellContainer unit="column" colWidth={26}>
          <EmployeeInfoDiv className="h-100 px-0">
            {/* <AvatarDiv>
            <NeuAvatar
              color="gray-60"
              style={{ height: '40px', width: '40px' }}
            >
              {sasToken ? (
                <EmployeeAvatar
                  src={
                    handleBlobPicUrl(employee.pictureUrl, sasToken) ??
                    userProfilePicUrl(employee.hcaid, sasToken)
                  }
                  alt="Employee Image"
                  onError={(e: any) => {
                    e.currentTarget.src = defaultImage;
                    return null;
                  }}
                />
              ) : (
                <EmployeeAvatar src={defaultImage} alt="Default Image" />
              )}
            </NeuAvatar>
          </AvatarDiv> */}
            <div>
              <StyledLabel className="neu-text-blue-70 bold">
                {employee.firstName} {employee.lastName}
              </StyledLabel>
              <StyledLabel>{employee.hcaid}</StyledLabel>
            </div>
          </EmployeeInfoDiv>
        </CellContainer>
        <CellContainer unit="column" colWidth={24}>
          <StyledLabel className="neu-text-gray-90">
            {employee.dept}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={14}>
          <DeptStartContainer>
            <StartDateContainer>
              {employee.effectiveStartDate &&
              employee.effectiveStartDate !== 'NULL'
                ? date.datePrettyString(employee.effectiveStartDate)
                : ''}
            </StartDateContainer>
            <NewHireIconContainer>
              {employee.newHire && (
                <CompleteIcon className="neu-text-blue-80">
                  auto_awesome
                </CompleteIcon>
              )}
            </NewHireIconContainer>
          </DeptStartContainer>
        </CellContainer>
        <CellContainer unit="column" colWidth={22}>
          <StyledLabel>
            {employee.lastRoundDate &&
            employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown'
              ? employee.lastRoundedBy
              : employee.lastRoundDate &&
                employee.lastRoundedBy &&
                employee.lastRoundedBy === 'Unknown'
              ? 'Unknown'
              : ''}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={14}>
          <StyledLabel>
            {employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown' &&
            !employee.lastRoundDate
              ? 'Unknown'
              : employee.lastRoundedBy &&
                employee.lastRoundDate &&
                date.isOrbitTimestamp(employee.lastRoundDate)
              ? format(date.parseDate(employee.lastRoundDate), 'MM/dd/yyyy') // make unknown if name and no date
              : ''}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    )
  );

  return (
    <Table
      customClass="adHoc-list-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableEmptySearch}
      loading={isLoading}
      type="adHocEmployeeList"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default AdhocEmployeesTable;
